import { useState } from 'react';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  EcranEnErreur,
  EnteteEcran,
  MessageErreur,
  couleurPastilleSite,
} from '../components/ui/Primitives';
import { useToast } from '../components/ui/useToast';
import { ErreurApi } from '../lib/api';
import { useAttribuerSites, useAttributions, type MembreAttribue } from '../lib/useAttributions';

const TITRE = 'Attribution des sites';

function trie(codes: readonly string[]): string[] {
  return [...codes].sort((a, b) => a.localeCompare(b, 'fr'));
}

function memes(a: readonly string[], b: readonly string[]): boolean {
  return trie(a).join(',') === trie(b).join(',');
}

function initiales(membre: MembreAttribue): string {
  return `${membre.prenom.charAt(0)}${membre.nom.charAt(0)}`.toUpperCase();
}

function PastilleSite({ code }: { code: string }): JSX.Element {
  return <span className={`rounded px-2 py-0.5 text-xs font-bold ${couleurPastilleSite(code)}`}>{code}</span>;
}

/**
 * Attribution des antennes aux membres du pôle pédagogique (CDC §10), par la
 * direction : elles font leur périmètre de travail. L'assistant(e) de site
 * n'en a qu'une ; l'API le dit pour chaque membre et refuse le reste.
 */
export function DirAttributionsPage(): JSX.Element {
  const toast = useToast();
  const { data, isLoading, error } = useAttributions();
  const attribuer = useAttribuerSites();
  const [membreId, setMembreId] = useState<string | null>(null);
  const [choix, setChoix] = useState<string[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  if (isLoading) return <Chargement libelle="Chargement de l’équipe pédagogique…" />;
  if (error || !data) {
    return (
      <EcranEnErreur
        titre={TITRE}
        message={error instanceof ErreurApi ? error.message : 'L’équipe pédagogique n’a pas pu être chargée.'}
      />
    );
  }

  const membre = data.membres.find((un) => un.id === membreId) ?? data.membres[0] ?? null;
  const sites = choix ?? membre?.sites ?? [];
  const modifie = membre !== null && !memes(sites, membre.sites);

  const choisirMembre = (id: string) => {
    setMembreId(id);
    setChoix(null);
    setErreur(null);
  };

  const basculer = (code: string) => {
    setErreur(null);
    if (membre?.uneSeuleAntenne) {
      setChoix([code]);
      return;
    }
    if (sites.includes(code)) {
      if (sites.length === 1) {
        setErreur('Attribuez au moins une antenne.');
        return;
      }
      setChoix(sites.filter((site) => site !== code));
    } else {
      setChoix([...sites, code]);
    }
  };

  const enregistrer = async () => {
    if (!membre) return;
    setErreur(null);
    try {
      await attribuer.mutateAsync({ utilisateurId: membre.id, sites });
      setChoix(null);
      toast(`Antennes de ${membre.prenom} ${membre.nom} enregistrées`);
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Les antennes n’ont pas pu être enregistrées.');
    }
  };

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre={TITRE}
        sousTitre="Direction · définition du périmètre de chaque membre du pôle pédagogique"
        actions={
          <BoutonPrincipal onClick={() => void enregistrer()} disabled={!modifie || attribuer.isPending}>
            {attribuer.isPending ? 'Enregistrement…' : 'Enregistrer les attributions'}
          </BoutonPrincipal>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <Carte titre="Périmètre de travail" sansBordure>
            {membre === null ? (
              <p className="p-4 text-sm text-codialis-gray sm:p-6">Aucun membre actif dans le pôle pédagogique.</p>
            ) : (
              <div className="space-y-5 p-4 sm:p-6">
                <label className="block min-w-0">
                  <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
                    Membre du pôle pédagogique
                  </span>
                  <select
                    value={membre.id}
                    onChange={(e) => choisirMembre(e.target.value)}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-codialis-navy sm:w-auto"
                  >
                    {data.membres.map((un) => (
                      <option key={un.id} value={un.id}>
                        {un.prenom} {un.nom} · {un.fonction}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="flex items-center gap-3">
                  <div
                    aria-hidden="true"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 text-xs font-bold text-white"
                  >
                    {initiales(membre)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-codialis-navy">
                      {membre.prenom} {membre.nom}
                    </div>
                    <div className="text-[11px] text-codialis-gray">
                      {membre.fonction} · {sites.length} site(s) attribué(s)
                      {membre.uneSeuleAntenne ? ' · une seule antenne, son site de rattachement' : ''}
                    </div>
                  </div>
                </div>

                <fieldset className="min-w-0">
                  <legend className="sr-only">Antennes attribuées à {membre.prenom} {membre.nom}</legend>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {data.antennes.map((antenne) => (
                      <label
                        key={antenne.code}
                        className="flex cursor-pointer items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 transition hover:bg-gray-100/70"
                      >
                        <input
                          type={membre.uneSeuleAntenne ? 'radio' : 'checkbox'}
                          name="antennes"
                          checked={sites.includes(antenne.code)}
                          onChange={() => basculer(antenne.code)}
                          className="h-4 w-4 rounded text-codialis-mint focus:ring-codialis-mint"
                        />
                        <span className="min-w-0 flex-1 text-sm font-medium text-codialis-navy">
                          {antenne.nom} ({antenne.code})
                        </span>
                        <PastilleSite code={antenne.code} />
                      </label>
                    ))}
                  </div>
                </fieldset>

                {!membre.uneSeuleAntenne && (
                  <div className="flex flex-wrap gap-2">
                    <BoutonSecondaire onClick={() => setChoix(data.antennes.map((antenne) => antenne.code))}>
                      Tous les sites
                    </BoutonSecondaire>
                  </div>
                )}

                {erreur && <MessageErreur message={erreur} />}

                <div className="rounded-xl bg-codialis-mint/10 p-4 text-xs leading-relaxed text-codialis-navy">
                  Les sites cochés définissent le périmètre de travail : tableau de bord, reporting, procès-verbaux,
                  sessions et alertes portent sur ces sites, dès l’enregistrement.
                </div>
              </div>
            )}
          </Carte>

          <Carte titre="Équipe pédagogique et périmètres" sansBordure>
            <div className="relative overflow-x-auto" role="region" aria-label="Équipe pédagogique et périmètres" tabIndex={0}>
              <table className="w-full min-w-[560px] text-sm">
                <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-codialis-gray">
                  <tr>
                    <th className="px-4 py-3 text-left font-bold">Membre</th>
                    <th className="px-4 py-3 text-left font-bold">Fonction</th>
                    <th className="px-4 py-3 text-left font-bold">Sites attribués</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.membres.map((un) => (
                    <tr
                      key={un.id}
                      aria-current={un.id === membre?.id || undefined}
                      className={`transition hover:bg-gray-50 ${un.id === membre?.id ? 'bg-gray-50' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => choisirMembre(un.id)}
                          className="text-left font-semibold text-codialis-navy hover:underline"
                        >
                          {un.prenom} {un.nom}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-xs text-codialis-gray">{un.fonction}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {un.sites.map((code) => (
                            <PastilleSite key={code} code={code} />
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Carte>
        </div>

        <div className="min-w-0 space-y-6">
          <Carte titre="Gouvernance multi-sites" sansBordure>
            <div className="space-y-3 p-4 text-sm text-codialis-gray sm:p-6">
              <p>Chaque dossier apprenant, session et fiche de présence est attaché à son antenne de rattachement.</p>
              <div className="space-y-2 rounded-xl bg-gray-50 p-3 text-xs">
                <div className="font-semibold text-codialis-navy">Règles automatiques :</div>
                <ul className="list-inside list-disc space-y-1 text-codialis-gray">
                  <li>Les assistants sont rattachés à leur site physique.</li>
                  <li>Le responsable pédagogique peut superviser un ou plusieurs sites.</li>
                  <li>La Direction dispose de la vue consolidée globale.</li>
                </ul>
              </div>
            </div>
          </Carte>
        </div>
      </div>
    </div>
  );
}
